import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export class HeroAnimation {
  constructor() {
    this.heroSection = document.querySelector(".hero");
    this.titleWords = document.querySelectorAll(".hero__title-word");
    this.subtitleWords = document.querySelectorAll(".hero__subtitle-word");
    this.logoBadge = document.querySelector(".hero__logo-badge");
    this.burgerImage = document.querySelector(".hero__image-container--burger");
    this.saladImage = document.querySelector(".hero__image-container--salad");
    this.ctaButton = document.querySelector(".hero__cta");
    this.ticker = document.querySelector(".hero__ticker");
    this.tickerContent = document.querySelector(".hero__ticker-content");
    this.tickerTween = null;
    this.tickerInitRetryCount = 0;
    this.tickerSpeedPxPerSecond = 100;
    this.onResize = this.handleResize.bind(this);
    this.masterTimeline = gsap.timeline();
    this.isMobile =
      window.innerWidth <= 768 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    this.init();
  }
  init() {
    if (!this.heroSection) return;

    if (!this.isMobile) this.splitTextIntoLetters();
    this.setInitialStates();
    this.createEntranceAnimations();
    this.setupSimpleTicker();
    window.addEventListener("resize", this.onResize);
  }
  splitTextIntoLetters() {
    this.titleWords.forEach((t) => {
      const r = t.textContent;
      t.innerHTML = "";
      [...r].forEach((e, i) => {
        const n = document.createElement("span");
        n.textContent = e === " " ? "\u00A0" : e;
        n.classList.add("hero__title-letter");
        n.style.display = "inline-block";
        t.appendChild(n);
      });
    });
  }
  setInitialStates() {
    gsap.set(this.titleWords, {
      y: 120,
      opacity: 0,
      rotationX: 45,
      transformOrigin: "50% 100%",
    });
    gsap.set(this.subtitleWords, {
      y: 80,
      opacity: 0,
      scale: 0.8,
      rotationY: 15,
    });
    gsap.set(this.logoBadge, { scale: 0, opacity: 0 });
    gsap.set(this.burgerImage, {
      y: 250,
      x: -100,
      opacity: 0,
      scale: 0.5,
      rotation: -25,
      transformOrigin: "center center",
    });
    gsap.set(this.saladImage, {
      y: 250,
      x: 100,
      opacity: 0,
      scale: 0.5,
      rotation: 25,
      transformOrigin: "center center",
    });
    gsap.set(this.ctaButton, { y: 50, opacity: 0, scale: 0.9 });
    gsap.set(this.ticker, { y: 100, opacity: 0 });
  }
  createEntranceAnimations() {
    this.masterTimeline
      .to(
        this.titleWords,
        {
          duration: 1.4,
          y: 0,
          opacity: 1,
          rotationX: 0,
          ease: "back.out(1.7)",
          stagger: 0.2,
        },
        0.3
      )
      .to(
        this.logoBadge,
        { duration: 1.2, scale: 1, opacity: 1, ease: "back.out(1.4)" },
        0.8
      )
      .to(
        this.subtitleWords,
        {
          duration: 1.2,
          y: 0,
          opacity: 1,
          scale: 1,
          rotationY: 0,
          ease: "power3.out",
          stagger: { amount: 0.6, from: "center" },
        },
        1
      )
      .to(
        this.burgerImage,
        {
          duration: 2.2,
          y: 0,
          x: 0,
          opacity: 1,
          scale: 1,
          rotation: 0,
          ease: "elastic.out(1, 0.6)",
          transformOrigin: "center center",
        },
        1.2
      )
      .to(
        this.saladImage,
        {
          duration: 2.2,
          y: 0,
          x: 0,
          opacity: 1,
          scale: 1,
          rotation: 0,
          ease: "elastic.out(1, 0.6)",
          transformOrigin: "center center",
        },
        1.4
      )
      .to(
        this.ctaButton,
        { duration: 1, y: 0, opacity: 1, scale: 1, ease: "back.out(1.4)" },
        1.8
      )
      .to(
        this.ticker,
        { duration: 1.2, y: 0, opacity: 1, ease: "power2.out" },
        2
      );
    gsap.delayedCall(3, () => {
      this.setupFloatingAnimations();
      this.setupTextAnimations();
      this.setupTextScrollAnimations();
    });
  }
  setupTextAnimations() {
    if (this.isMobile) return;
    document.querySelectorAll(".hero__title-letter").forEach((r, e) => {
      gsap.to(r, {
        duration: 0.6,
        y: -8,
        ease: "bounce.out",
        repeat: -1,
        repeatDelay: 3 + e * 0.1,
        delay: e * 0.05,
      });
    });
    this.subtitleWords.forEach((r, e) => {
      switch (e % 3) {
        case 0:
          gsap.to(r, {
            duration: 2,
            scale: 1.05,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true,
            delay: e * 0.3,
          });
          break;
        case 1:
          gsap.to(r, {
            duration: 3,
            y: -5,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true,
            delay: e * 0.3,
          });
          break;
        case 2:
          gsap.to(r, {
            duration: 3.5,
            scale: 1.02,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true,
            delay: e * 0.3,
          });
          break;
        default:
          break;
      }
    });
    if (this.logoBadge)
      gsap.to(this.logoBadge, {
        duration: 8,
        scale: 1.05,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    if (this.ctaButton)
      gsap.to(this.ctaButton, {
        duration: 2.5,
        scale: 1.02,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
  }
  setupFloatingAnimations() {
    if (this.burgerImage)
      gsap.to(this.burgerImage, {
        duration: 3.5,
        y: "+=25",
        rotation: "+=8",
        scale: "+=0.05",
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    if (this.saladImage)
      gsap.to(this.saladImage, {
        duration: 4,
        y: "+=30",
        rotation: "-=10",
        scale: "+=0.08",
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        delay: 2,
      });
  }
  setupSimpleTicker() {
    if (!this.tickerContent) return;

    // Duplicate once so the second half starts exactly where the first ends.
    if (!this.tickerContent.dataset.loopReady) {
      this.tickerContent.innerHTML += this.tickerContent.innerHTML;
      this.tickerContent.dataset.loopReady = "true";
    }

    const oneLoopDistance = this.tickerContent.scrollWidth / 2;
    if (!oneLoopDistance) {
      if (this.tickerInitRetryCount < 20) {
        this.tickerInitRetryCount += 1;
        gsap.delayedCall(0.1, () => this.setupSimpleTicker());
      }
      return;
    }

    this.tickerInitRetryCount = 0;

    if (this.tickerTween) this.tickerTween.kill();
    gsap.set(this.tickerContent, { x: 0 });
    const duration = oneLoopDistance / this.tickerSpeedPxPerSecond;
    this.tickerTween = gsap.to(this.tickerContent, {
      duration,
      x: -oneLoopDistance,
      ease: "none",
      repeat: -1,
    });
  }
  handleResize() {
    if (this.tickerContent) {
      this.tickerInitRetryCount = 0;
      gsap.delayedCall(0.05, () => this.setupSimpleTicker());
    }
  }
  setupTextScrollAnimations() {
    if (this.isMobile) return;
    gsap.delayedCall(1, () => {
      this.titleWords.forEach((t, r) => {
        gsap.to(t, {
          y: 150,
          ease: "none",
          scrollTrigger: {
            trigger: this.heroSection,
            start: "top top",
            end: "bottom center",
            scrub: 1.5,
          },
        });
      });
      this.subtitleWords.forEach((t, r) => {
        gsap.to(t, {
          y: -180,
          ease: "none",
          scrollTrigger: {
            trigger: this.heroSection,
            start: "top top",
            end: "bottom center",
            scrub: 2,
          },
        });
      });
      if (this.logoBadge)
        gsap.to(this.logoBadge, {
          y: -40,
          ease: "none",
          scrollTrigger: {
            trigger: this.heroSection,
            start: "top top",
            end: "bottom center",
            scrub: 1.8,
          },
        });
      if (this.ctaButton)
        gsap.to(this.ctaButton, {
          y: -60,
          ease: "none",
          scrollTrigger: {
            trigger: this.heroSection,
            start: "top top",
            end: "bottom center",
            scrub: 1.3,
          },
        });
      if (this.burgerImage)
        gsap.to(this.burgerImage, {
          y: -50,
          ease: "none",
          scrollTrigger: {
            trigger: this.heroSection,
            start: "top top",
            end: "bottom center",
            scrub: 1,
          },
        });
      if (this.saladImage)
        gsap.to(this.saladImage, {
          y: -70,
          ease: "none",
          scrollTrigger: {
            trigger: this.heroSection,
            start: "top top",
            end: "bottom center",
            scrub: 1.2,
          },
        });
    });
  }
}